import { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { captureException } from '@sentry/nextjs';
import api from 'src/utils/api';
import i18n from 'src/utils/translate';
import { helpScoutArticle } from 'src/utils/helpScoutBeacon';
import { EMAIL } from 'src/constants/regex';
import { DefaultLayout } from 'src/components/layouts';
import { PrimaryButton, LinkButton } from 'styles-js/buttons';
import { Input } from 'styles-js/form';
import { Container, Title, Label, StyledError } from 'src/modules/resetPassword/sharedStyles';

export default function RequestResetPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data: { email: string }) => {
    try {
      await api.postPublic('password-reset', { email: data.email });
      setEmail(data.email);
    } catch (ex) {
      captureException(ex);
    }
  };

  return (
    <DefaultLayout>
      {!email ? (
        <Container onSubmit={handleSubmit(onSubmit)}>
          <Title>{i18n.t(`Enter your email and we'll send you a link to reset your password`)}</Title>
          <Label htmlFor="email">{i18n.t('Email address')}:</Label>
          <Input
            id="email"
            isInvalid={!!errors.email}
            {...register('email', {
              pattern: EMAIL,
              required: true,
            })}
          />
          {errors.email?.type === 'required' && <StyledError>{i18n.t('Email address required')}</StyledError>}
          {errors.email?.type === 'pattern' && <StyledError>{i18n.t('Invalid email address')}</StyledError>}
          <PrimaryButton type="submit">{i18n.t('Send link to my email')}</PrimaryButton>
        </Container>
      ) : (
        <Container as="div">
          <Text>{i18n.t('If an account exists for %1$s, you will get an email with instructions on resetting your password.').replace('%1$s', email)}</Text>
          <Text>{i18n.t(`If it doesn't arrive, be sure to  check your spam folder or try a different email.`)}</Text>
          <LinkButton onClick={() => helpScoutArticle('184-why-didnt-i-receive-a-password-reset-email')}>{i18n.t('Learn more')}{' >'}</LinkButton>
        </Container>
      )}
    </DefaultLayout>
  );
}

const Text = styled.p`
  &:first-child {
    margin-bottom: 16px;
  }
`;
